import { MessageReactionName } from "@redotech/redo-agents-cards/support-agents/message-reaction";
import { Schema } from "effect";
import { MongoUserId } from "../../../../ids/mongo-ids";
import { EmailAddressEntry } from "./email";
import {
  MessageFacebookCommentAttribute,
  MessageFacebookCommentState,
} from "./facebook-comment";
import {
  MessageInstagramCommentAttribute,
  MessageInstagramCommentState,
} from "./instagram-comment";
import {
  MessageMetaCommentAttribute,
  MessageMetaCommentState,
  MessageMetaCommentType,
} from "./meta-comment";
import { MessageRedoChatConfirmationState } from "./redo-chat";
import { MessageSmsService } from "./sms";

/**
 * Max length of HTML stored inline in the `parsed_html_body` column. Email
 * HTML at or below this size is persisted directly in the DB (reply threads
 * and all); larger bodies are offloaded to S3 and referenced by `htmlBodyUrl`.
 * Guards the DB-write path (the repo models); the read path is uncapped so it
 * can materialize the full S3 body back into `parsedHtmlBody`.
 */
export const MAX_PARSED_HTML_BODY_LENGTH = 64 * 1024;

// Gmail channel data
export const GmailChannelData = Schema.Struct({
  _tag: Schema.Literal("gmail"),
  gmailMessageId: Schema.String,
  gmailHistoryId: Schema.NullOr(Schema.String),
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /**
   * S3 URL to the full HTML body, populated ONLY when the HTML exceeds
   * MAX_PARSED_HTML_BODY_LENGTH and is stored in S3 instead of inline. When
   * set, S3 is the source of truth; when null, `parsedHtmlBody` holds the
   * full HTML.
   */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  /**
   * Full HTML body of the email (reply threads included), stored inline when
   * it fits within MAX_PARSED_HTML_BODY_LENGTH; null when offloaded to S3 (see
   * `htmlBodyUrl`). On read this is the materialized full body (uncapped here
   * so the S3 source of truth can be inlined). Plain text for display/search
   * is derived separately by the email body parser.
   */
  parsedHtmlBody: Schema.NullOr(Schema.String),
  /**
   * Email addresses associated with this message (from, to, cc, bcc).
   */
  addresses: Schema.Array(EmailAddressEntry),
});
export type GmailChannelData = typeof GmailChannelData.Type;

// Outlook channel data
export const OutlookChannelData = Schema.Struct({
  _tag: Schema.Literal("outlook"),
  outlookMessageId: Schema.String,
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /**
   * S3 URL to the full HTML body, populated ONLY when the HTML exceeds
   * MAX_PARSED_HTML_BODY_LENGTH and is stored in S3 instead of inline. When
   * set, S3 is the source of truth; when null, `parsedHtmlBody` holds the
   * full HTML.
   */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  /**
   * Full HTML body of the email (reply threads included), stored inline when
   * it fits within MAX_PARSED_HTML_BODY_LENGTH; null when offloaded to S3 (see
   * `htmlBodyUrl`). On read this is the materialized full body (uncapped here
   * so the S3 source of truth can be inlined). Plain text for display/search
   * is derived separately by the email body parser.
   */
  parsedHtmlBody: Schema.NullOr(Schema.String),
  /**
   * Email addresses associated with this message (from, to, cc, bcc).
   */
  addresses: Schema.Array(EmailAddressEntry),
});
export type OutlookChannelData = typeof OutlookChannelData.Type;

// Custom email channel data (Postmark-based SMTP)
export const CustomEmailChannelData = Schema.Struct({
  _tag: Schema.Literal("customEmail"),
  customEmailMessageId: Schema.NullOr(Schema.String),
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /** See {@link GmailChannelData} for the htmlBodyUrl / parsedHtmlBody contract. */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  parsedHtmlBody: Schema.NullOr(Schema.String),
  addresses: Schema.Array(EmailAddressEntry),
});
export type CustomEmailChannelData = typeof CustomEmailChannelData.Type;

// Email channel data - union of Gmail, Outlook, and CustomEmail
export const EmailChannelData = Schema.Union(
  GmailChannelData,
  OutlookChannelData,
  CustomEmailChannelData,
);
export type EmailChannelData = typeof EmailChannelData.Type;

// SMS channel data. Agent tapback `reaction` lives here (not on the base
// message) because tapbacks are an SMS/iMessage-channel concept; null when none.
export const SmsChannelData = Schema.Struct({
  _tag: Schema.Literal("sms"),
  messageService: MessageSmsService,
  reaction: Schema.optional(Schema.NullOr(Schema.Enums(MessageReactionName))),
});
export type SmsChannelData = typeof SmsChannelData.Type;

// TikTok channel data. The tiktok message id lives on `message.external_message_id`
// like Facebook DM, so no extra side-row fields are needed here.
export const TiktokChannelData = Schema.Struct({
  _tag: Schema.Literal("tiktok"),
});
export type TiktokChannelData = typeof TiktokChannelData.Type;

// Mobile message (iMessage) channel data. Backed by the same `message_sms`
// subtable as SMS, so the agent tapback `reaction` lives here too; null when none.
export const MobileMessageChannelData = Schema.Struct({
  _tag: Schema.Literal("mobileMessage"),
  reaction: Schema.optional(Schema.NullOr(Schema.Enums(MessageReactionName))),
});
export type MobileMessageChannelData = typeof MobileMessageChannelData.Type;

// Redo Chat channel data
export const RedoChatChannelData = Schema.Struct({
  _tag: Schema.Literal("redoChat"),
  contentType: Schema.String,
  structuredContent: Schema.NullOr(Schema.Unknown),
  confirmationState: Schema.NullOr(MessageRedoChatConfirmationState),
  pendingToolCallId: Schema.NullOr(Schema.String),
});
export type RedoChatChannelData = typeof RedoChatChannelData.Type;

// Voice channel data
export const VoiceDirection = Schema.Literal("inbound", "outbound");
export type VoiceDirection = typeof VoiceDirection.Type;

export const VoiceStatus = Schema.Literal(
  "ringing",
  "answered",
  "missed",
  "voicemail",
  "completed",
  "failed",
);
export type VoiceStatus = typeof VoiceStatus.Type;

export const VoiceChannelData = Schema.Struct({
  _tag: Schema.Literal("voice"),
  direction: VoiceDirection,
  status: VoiceStatus,
  telnyxCallControlId: Schema.String,
  telnyxRecordingId: Schema.NullOr(Schema.String),
  recordingUrl: Schema.NullOr(Schema.String),
  transcriptUrl: Schema.NullOr(Schema.String),
  durationSeconds: Schema.NullOr(Schema.Number),
  startedAt: Schema.Date,
  connectedAt: Schema.NullOr(Schema.Date),
  endedAt: Schema.NullOr(Schema.Date),
});
export type VoiceChannelData = typeof VoiceChannelData.Type;

// Facebook comment channel data
export const FacebookCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("facebookComment"),
  state: Schema.NullOr(MessageFacebookCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageFacebookCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type FacebookCommentChannelData = typeof FacebookCommentChannelData.Type;

// Instagram comment channel data
export const InstagramCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("instagramComment"),
  state: Schema.NullOr(MessageInstagramCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageInstagramCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type InstagramCommentChannelData =
  typeof InstagramCommentChannelData.Type;

// Meta comment channel data (for facebook and instagram comments) - DEPRECATED
export const MetaCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("metaComment"),
  type: MessageMetaCommentType,
  state: Schema.NullOr(MessageMetaCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageMetaCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type MetaCommentChannelData = typeof MetaCommentChannelData.Type;

// Facebook DM channel data
export const FacebookDmChannelData = Schema.Struct({
  _tag: Schema.Literal("facebookDm"),
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
});
export type FacebookDmChannelData = typeof FacebookDmChannelData.Type;

// Instagram DM channel data
export const InstagramDmChannelData = Schema.Struct({
  _tag: Schema.Literal("instagramDm"),
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
});
export type InstagramDmChannelData = typeof InstagramDmChannelData.Type;

// Internal note channel data
export const InternalNoteChannelData = Schema.Struct({
  _tag: Schema.Literal("internalNote"),
  htmlContent: Schema.NullOr(Schema.String),
  mentionedMongoUserIds: Schema.NullOr(Schema.Array(MongoUserId)),
});
export type InternalNoteChannelData = typeof InternalNoteChannelData.Type;

// Contact form custom field (merchant-defined fields beyond the standard set)
export const ContactFormCustomField = Schema.Struct({
  label: Schema.String,
  value: Schema.String,
});
export type ContactFormCustomField = typeof ContactFormCustomField.Type;

export const ContactFormSource = Schema.Literal("redo", "shopify");
export type ContactFormSource = typeof ContactFormSource.Type;

// Contact form channel data. `comment` lives on `message.content` — not
// duplicated here.
export const ContactFormChannelData = Schema.Struct({
  _tag: Schema.Literal("contactForm"),
  source: Schema.optionalWith(ContactFormSource, { default: () => "redo" }),
  name: Schema.NullOr(Schema.String),
  email: Schema.String,
  phone: Schema.NullOr(Schema.String),
  subject: Schema.NullOr(Schema.String),
  customFields: Schema.Array(ContactFormCustomField),
});
export type ContactFormChannelData = typeof ContactFormChannelData.Type;

// Discriminated union of all message channel data types
export const MessageChannelData = Schema.Union(
  GmailChannelData,
  OutlookChannelData,
  CustomEmailChannelData,
  SmsChannelData,
  TiktokChannelData,
  MobileMessageChannelData,
  RedoChatChannelData,
  VoiceChannelData,
  FacebookCommentChannelData,
  InstagramCommentChannelData,
  MetaCommentChannelData,
  FacebookDmChannelData,
  InstagramDmChannelData,
  InternalNoteChannelData,
  ContactFormChannelData,
);
export type MessageChannelData = typeof MessageChannelData.Type;
